import type { ReactNode } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';

/**
 * Carte KPI du dashboard KPI consolidés. Design Stripe-like :
 *  - Card blanche radius 8px border 1px
 *  - Label uppercase micro (text-2xs tracking-wide text-stripe-text-muted)
 *  - Valeur principale large (text-2xl font-semibold tracking-tight)
 *  - Sub-label explicatif optionnel
 *  - Pill variation ↑ / ↓ colorée selon le sens (vert = up, rouge = down)
 */
export interface StatCardProps {
  label: string;
  value: ReactNode;
  sub?: string;
  /** Variation en pourcentage vs N-1. null = pas de comparaison dispo. */
  variation?: number | null;
  /** Pour les KPI où "baisse" est positif (ex: pertes). Défaut : false. */
  inverseVariation?: boolean;
}

export function StatCard({ label, value, sub, variation, inverseVariation }: StatCardProps) {
  const showVariation = variation !== undefined && variation !== null;
  const isUp = (variation ?? 0) > 0;
  const isDown = (variation ?? 0) < 0;
  const isPositive = inverseVariation ? isDown : isUp;
  const isNegative = inverseVariation ? isUp : isDown;

  return (
    <div className="animate-fade-in rounded-stripe border border-stripe-border bg-white p-4 shadow-stripe-sm">
      <div className="text-2xs font-medium uppercase tracking-wide text-stripe-text-muted">
        {label}
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="truncate text-2xl font-semibold tracking-tight text-stripe-text">
          {value}
        </span>
        {showVariation && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-2xs font-medium ${
              isPositive
                ? 'bg-emerald-50 text-emerald-600'
                : isNegative
                  ? 'bg-red-50 text-red-600'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(variation ?? 0).toFixed(1)}%
          </span>
        )}
      </div>
      {sub && <div className="mt-0.5 text-2xs text-stripe-text-muted">{sub}</div>}
    </div>
  );
}
